import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';
import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/tag/tag.js';

import { ApiError, api, formValues, leaveKindLabels, type TimesheetLeaveDay, type VacationAnchor } from '../api.js';
import { pageStyles } from '../shared-styles.js';

/**
 * Leave days of one employee in a month (Everhour and manual) plus the
 * vacation balance anchors. dialog.show(employeeId, name, from, to).
 */
@customElement('leave-dialog')
export class LeaveDialog extends LitElement {
  static styles = pageStyles;

  @state() private isOpen = false;
  @state() private employeeId = 0;
  @state() private name = '';
  @state() private from = '';
  @state() private to = '';
  @state() private days: TimesheetLeaveDay[] = [];
  @state() private anchors: VacationAnchor[] = [];
  @state() private error = '';
  @state() private saving = false;

  async show(employeeId: number, name: string, from: string, to: string) {
    this.employeeId = employeeId;
    this.name = name;
    this.from = from;
    this.to = to;
    this.error = '';
    this.isOpen = true;
    await this.load();
  }

  private async load() {
    try {
      [this.days, this.anchors] = await Promise.all([
        api.get<TimesheetLeaveDay[]>(`/api/leave?employee_id=${this.employeeId}&from=${this.from}&to=${this.to}`),
        api.get<VacationAnchor[]>(`/api/vacation-anchors?employee_id=${this.employeeId}`),
      ]);
    } catch (e) {
      this.error = (e as Error).message;
    }
  }

  private onHidden = (e: Event) => {
    if (e.target === e.currentTarget) {
      this.isOpen = false;
      this.dispatchEvent(new CustomEvent('closed', { bubbles: true, composed: true }));
    }
  };

  private async addDay(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const v = formValues(form);
    await this.call(async () => {
      await api.post('/api/leave', { employee_id: this.employeeId, date: v.date, kind: v.kind ?? 'vacation', fraction: v.fraction ?? '1', note: v.note });
      form.reset();
    });
  }

  private async removeDay(d: TimesheetLeaveDay) {
    await this.call(() => api.delete(`/api/leave/${d.id}`));
  }

  private async addAnchor(e: Event) {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const v = formValues(form);
    await this.call(async () => {
      await api.post('/api/vacation-anchors', { employee_id: this.employeeId, as_of: v.as_of, remaining_days: v.remaining_days, note: v.note });
      form.reset();
    });
  }

  private async removeAnchor(a: VacationAnchor) {
    await this.call(() => api.delete(`/api/vacation-anchors/${a.id}`));
  }

  private async call(fn: () => Promise<unknown>) {
    this.saving = true;
    this.error = '';
    try {
      await fn();
      await this.load();
    } catch (err) {
      if (err instanceof ApiError) {
        const hint = Object.values(err.fields)[0];
        this.error = hint ? `${err.message}: ${hint}` : err.message;
      } else {
        this.error = (err as Error).message;
      }
    } finally {
      this.saving = false;
    }
  }

  render() {
    return html`
      <wa-dialog label=${`Leave · ${this.name} · ${this.from.slice(0, 7)}`} ?open=${this.isOpen} @wa-after-hide=${this.onHidden} style="--width: min(52rem, calc(100vw - 1.5rem));">
        ${this.error ? html`<wa-callout variant="danger">${this.error}</wa-callout>` : nothing}

        <h4>Days off this month</h4>
        ${this.days.length === 0 ? html`<p class="muted">None.</p>` : nothing}
        ${this.days.length
          ? html`
              <div class="table-wrap"><table class="entries">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Kind</th>
                    <th class="num">Days</th>
                    <th>Source</th>
                    <th>Note</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  ${this.days.map(
                    (d) => html`
                      <tr>
                        <td>${d.date}</td>
                        <td>${leaveKindLabels[d.kind]}</td>
                        <td class="num">${d.fraction}</td>
                        <td>${d.source === 'everhour' ? html`Everhour${d.client_name ? ` (${d.client_name})` : ''}` : 'manual'}</td>
                        <td>${d.note ?? html`<span class="muted">—</span>`}</td>
                        <td class="actions">
                          ${d.source === 'manual'
                            ? html`<wa-button size="small" appearance="plain" variant="danger" ?disabled=${this.saving} @click=${() => this.removeDay(d)}>Remove</wa-button>`
                            : html`<span class="sub">change in Everhour</span>`}
                        </td>
                      </tr>
                    `,
                  )}
                </tbody>
              </table></div>
            `
          : nothing}

        <form @submit=${this.addDay}>
          <h4>Add a manual day</h4>
          <wa-input name="date" label="Date" type="date" required min=${this.from} max=${this.to}></wa-input>
          <wa-select name="kind" label="Kind" value="vacation">
            ${Object.entries(leaveKindLabels).map(([k, label]) => html`<wa-option value=${k}>${label}</wa-option>`)}
          </wa-select>
          <wa-select name="fraction" label="Portion" value="1">
            <wa-option value="1">Full day</wa-option>
            <wa-option value="0.5">Half day</wa-option>
            <wa-option value="0.25">Quarter day</wa-option>
          </wa-select>
          <wa-input name="note" label="Note"></wa-input>
          <div class="full"><wa-button type="submit" size="small" appearance="outlined" ?loading=${this.saving}>Add day</wa-button></div>
        </form>

        <form @submit=${this.addAnchor}>
          <h4>Vacation balance anchors</h4>
          <div class="full">
            ${this.anchors.length === 0 ? html`<p class="muted">No anchor yet; the balance cannot be computed. Add the remaining days as of a date; vacation taken from that date on is subtracted.</p>` : nothing}
            ${this.anchors.map(
              (a) => html`
                <div class="inline-field">
                  <span>${a.as_of}: <strong>${a.remaining_days}</strong> days remaining${a.note ? ` · ${a.note}` : ''}</span>
                  <wa-button size="small" appearance="plain" variant="danger" ?disabled=${this.saving} @click=${() => this.removeAnchor(a)}>Remove</wa-button>
                </div>
              `,
            )}
          </div>
          <wa-input name="as_of" label="As of" type="date" required></wa-input>
          <wa-input name="remaining_days" label="Remaining days" inputmode="decimal" required placeholder="12.5"></wa-input>
          <wa-input class="full" name="note" label="Note"></wa-input>
          <div class="full"><wa-button type="submit" size="small" appearance="outlined" ?loading=${this.saving}>Add anchor</wa-button></div>
        </form>
      </wa-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'leave-dialog': LeaveDialog;
  }
}
